// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime, timeUntil } from '@/components/greenlight/relativeTime';
import { SectionCard } from '@/components/greenlight/SectionCard';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { HEALTH_POLL_MS, POSTURE_KEY } from '@/lib/healthPoll';
import { cn } from '@/lib/utils';
import type { UpdaterPosture } from '@greenlight/shared';

import { SettingsSectionHeading } from './SettingsSectionHeading';

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex items-center justify-between gap-3 px-5 py-3.5">
      <span className="text-sm text-foreground">{label}</span>
      <span className="min-w-0 text-right text-sm font-medium text-foreground">{children}</span>
    </div>
  );
}

const Unknown = (): React.JSX.Element => (
  <span className="font-normal text-muted-foreground">unknown</span>
);

function PostureRows({ posture }: { posture: UpdaterPosture }): React.JSX.Element {
  return (
    <div className="divide-y divide-border">
      <Row label="Automatic updates">
        {posture.updates_enabled === null ? (
          <StatusBadge variant="inactive">Can&rsquo;t tell</StatusBadge>
        ) : posture.updates_enabled ? (
          <StatusBadge variant="active">On</StatusBadge>
        ) : (
          <StatusBadge variant="inactive">Off</StatusBadge>
        )}
      </Row>
      <Row label="Schedule">
        {posture.schedule !== null ? (
          <span className="inline-flex flex-wrap items-center justify-end gap-1.5">
            <MonoValue>{posture.schedule}</MonoValue>
            {posture.time_zone !== null && (
              <span className="text-muted-foreground">{posture.time_zone}</span>
            )}
          </span>
        ) : (
          <Unknown />
        )}
      </Row>
      <Row label="Next run">
        {posture.next_run_at !== null ? (
          <span title={posture.next_run_at}>{timeUntil(posture.next_run_at)}</span>
        ) : posture.updates_enabled === false ? (
          <span className="font-normal text-muted-foreground">not scheduled</span>
        ) : (
          <Unknown />
        )}
      </Row>
      <Row label="Last firing">
        {posture.last_scheduled_at !== null ? (
          <span title={posture.last_scheduled_at}>{relativeTime(posture.last_scheduled_at)}</span>
        ) : (
          <span className="font-normal text-muted-foreground">never</span>
        )}
      </Row>
      <Row label="Prereleases">
        {posture.include_prereleases === null ? (
          <Unknown />
        ) : posture.include_prereleases ? (
          'Takes prereleases as well as releases'
        ) : (
          'Takes releases only'
        )}
      </Row>
    </div>
  );
}

/**
 * The maintenance window, read-only. Every value here is an installer input the
 * chart renders onto the updater CronJob, so there is nothing on this page to
 * change, pause, skip, or approve — changing one means rerunning the packaged
 * installer (docs/26 §Schedule and policy).
 *
 * It reads the same live posture the Internal Health Updater panel does, rather
 * than a settings payload of its own: two sources for one schedule is how this
 * card came to show values nothing populated.
 */
export function MaintenanceSettings({ className }: { className?: string }): React.JSX.Element {
  const query = api.health.updaterPosture.useQuery(POSTURE_KEY, undefined, {
    queryKey: POSTURE_KEY,
    refetchInterval: HEALTH_POLL_MS,
  });
  const posture = query.data?.status === 200 ? query.data.body : null;

  return (
    <div className={cn('space-y-6', className)}>
      <SettingsSectionHeading title="Maintenance" pill="Org admin" />

      <SectionCard headerBar title="Update schedule">
        {query.isLoading ? (
          <div className="space-y-2 px-5 py-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-56" />
          </div>
        ) : query.isError || posture === null ? (
          <div className="px-5 py-4">
            <Alert variant="destructive">
              <AlertDescription>
                Couldn&rsquo;t read the update schedule. Retry in a moment.
              </AlertDescription>
            </Alert>
          </div>
        ) : (
          <PostureRows posture={posture} />
        )}
      </SectionCard>

      <p className="text-sm text-muted-foreground">
        Greenlight applies control-plane updates on this schedule. Apps stay up; brief control-plane
        blips are possible. Every run, its phases, and its full transcript are on Internal Health.
      </p>
    </div>
  );
}
